import React, {useState} from "react";
import {Link} from "react-router-dom";
import {ShoppingCart, Trash2} from "lucide-react";
import {useLandingCartSync} from "./landing/useLandingCart";
import {money} from "../lib/format";

/**
 * Shared cart icon used across every navbar (landing, course details, learner).
 * Renders the icon + live count badge and a hover dropdown listing the cart
 * contents with remove, total, and a "Go to cart" link. Reads the single
 * platform cart store, so the count and items are identical everywhere.
 */
export default function CartIcon({className = ""}) {
  const {cart, removeCourse} = useLandingCartSync();
  const [open, setOpen] = useState(false);

  const total = cart.reduce((sum, item) => sum + Number(item.price || 0), 0);

  return (
    <div
      className={`relative shrink-0 ${className}`}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <Link
        to="/cart"
        aria-label={cart.length > 0 ? `Cart, ${cart.length} item${cart.length === 1 ? "" : "s"}` : "Cart"}
        className="relative flex h-10 w-10 items-center justify-center rounded-full text-slate-600 transition-colors hover:bg-slate-100"
      >
        <ShoppingCart className="h-5 w-5" />
        {cart.length > 0 && (
          <span className="absolute -right-0.5 -top-0.5 flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-blue-900 px-1 text-[10px] font-bold text-white">
            {cart.length}
          </span>
        )}
      </Link>

      {open && (
        <div className="absolute right-0 top-full z-50 w-80 pt-2">
          <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-xl">
            <div className="border-b border-slate-100 px-4 py-2.5">
              <p className="text-sm font-semibold text-slate-900">
                Courses in cart {cart.length > 0 && `(${cart.length})`}
              </p>
            </div>

            {cart.length > 0 ? (
              <>
                <div className="max-h-72 overflow-y-auto p-2">
                  {cart.map((item) => (
                    <div
                      key={`${item.type || "course"}-${item.id}`}
                      className="flex items-center gap-3 rounded-md p-2 transition-colors hover:bg-slate-50"
                    >
                      <img
                        src={item.thumbnail || item.image || "/images/course_placeholder.jpg"}
                        alt={item.title}
                        className="h-11 w-16 shrink-0 rounded object-cover"
                      />
                      <div className="min-w-0 flex-1">
                        <p className="line-clamp-2 text-xs font-medium text-slate-800">
                          {item.title}
                        </p>
                        <p className="mt-0.5 text-xs font-semibold text-blue-900">
                          {money(item.price)}
                        </p>
                      </div>
                      <button
                        type="button"
                        aria-label="Remove from cart"
                        onClick={(event) => {
                          event.preventDefault();
                          removeCourse(item.id, item.type);
                        }}
                        className="shrink-0 rounded p-1 text-slate-400 transition-colors hover:bg-red-50 hover:text-red-600"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
                <div className="border-t border-slate-100 p-3">
                  <div className="mb-2 flex items-center justify-between text-sm">
                    <span className="text-slate-500">Total</span>
                    <span className="font-bold text-slate-900">{money(total)}</span>
                  </div>
                  <Link
                    to="/cart"
                    className="block rounded-md bg-blue-900 py-2 text-center text-sm font-semibold text-white transition-colors hover:bg-blue-800"
                  >
                    Go to cart
                  </Link>
                </div>
              </>
            ) : (
              <p className="px-4 py-6 text-center text-xs text-slate-500">Your cart is empty</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
